/**
 * The state data registry: where every non-Utah state's data lives once it is loaded.
 *
 * Why: the web app used to ship all eight states in one file (5.7 MB). Utah (the default) stays in the main chunk and in
 * every module's own table; each other state's pack, local layer, contacts, shapes, opening steps and plain words are one
 * bundle (states/bundles/state-xx.ts, generated by scripts/states/gen-bundles.ts) that the web app loads with a dynamic
 * import when the person's place is in that state (states/loaders.ts `ensureStateLoaded`). The rules engine and every
 * `xxxFor(state)` function stay synchronous: they read this registry.
 *
 * What a read of a state that is not loaded does: it THROWS `StateNotLoadedError`. It never answers with Utah's rules
 * under another state's name (a Texas kitchen shown Utah's law is worse than no answer). The app awaits
 * `ensureStateLoaded` before it shows any screen for a place, and shows a retry when the load fails.
 *
 * Servers, scripts, tests and the phone app import the core index, which installs all bundles at once (states/eager.ts):
 * for them every read works at once. Only the web build swaps that file for a no-op (apps/web/vite.config.ts).
 *
 * This module imports no state data, so any module may import it without a cycle. Modules that READ state data import
 * access.ts instead (it also makes sure the eager install has run).
 */
import { SUPPORTED_STATES, type UsState } from "./types.ts";

/** One state's installed data: namespace → name → value. `words` holds the per-module plain words by key ("LIBRARY"). */
export type StateData = Readonly<Record<string, Readonly<Record<string, unknown>>>>;

export class StateNotLoadedError extends Error {
  readonly state: UsState;
  readonly what: string;
  constructor(state: UsState, what: string) {
    super(`The ${state} rules are not loaded yet (${what}). Await ensureStateLoaded("${state}") before reading them.`);
    this.name = "StateNotLoadedError";
    this.state = state;
    this.what = what;
  }
}

const INSTALLED = new Map<UsState, StateData>();
let epoch = 0;
const watchers: ((state: UsState) => void)[] = [];

/** Utah is always there (it is compiled into every module's table); every other state is there once installed. */
export const isStateLoaded = (state: UsState): boolean => state === "UT" || INSTALLED.has(state);
/** The states whose data can be read now, in the pack order (Utah first). */
export const loadedStates = (): UsState[] => SUPPORTED_STATES.filter(isStateLoaded);
/** Bumps whenever a state is installed or removed: caches built from several states compare it. */
export const stateEpoch = (): number => epoch;
/** Call `fn` whenever a state is installed or removed (the rules world rebuilds, for one). */
export function onStatesChanged(fn: (state: UsState) => void): void { watchers.push(fn); }

/** Make a state's bundle readable. Installing twice is a no-op. Utah has no bundle. */
export function installState(state: UsState, data: StateData): void {
  if (state === "UT" || INSTALLED.has(state)) return;
  INSTALLED.set(state, data);
  epoch++;
  for (const w of watchers) w(state);
}

/** Tests only: forget a state, to prove a read of it fails loudly and the lazy path brings it back. */
export function uninstallStateForTest(state: UsState): StateData | undefined {
  const was = INSTALLED.get(state);
  if (INSTALLED.delete(state)) { epoch++; for (const w of watchers) w(state); }
  return was;
}

/** A loaded state's data, or a loud StateNotLoadedError (never another state's). */
export const loadedData = (state: UsState, what: string): StateData => {
  const d = INSTALLED.get(state);
  if (!d) throw new StateNotLoadedError(state, what);
  return d;
};

